// src/app/components/prompt-tracking/prompt-search-filter.tsx
// Search input + intent filter tabs + Add Prompt button.

import { Search, Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { INTENT_CONFIG } from '../../constants/prompt-tracking-constants';
import type { PromptIntent } from '../../types/prompt-tracking-types';

interface PromptSearchFilterProps {
  searchQuery: string;
  selectedIntent: PromptIntent | 'all';
  onSearchChange: (query: string) => void;
  onIntentChange: (intent: PromptIntent | 'all') => void;
  onAddPrompt: () => void;
}

export function PromptSearchFilter({
  searchQuery,
  selectedIntent,
  onSearchChange,
  onIntentChange,
  onAddPrompt,
}: PromptSearchFilterProps) {
  return (
    <div className="space-y-3 md:space-y-0 md:flex md:items-center md:gap-4 mb-5 md:mb-6">
      {/* Row 1 on mobile / left on desktop: search input (full-width on mobile) */}
      <div className="flex-1 relative">
        <Search
          className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
          style={{ color: 'var(--muted-foreground)' }}
        />
        <input
          type="text"
          placeholder="Search prompts..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-10 pr-4 py-2 rounded-lg border"
          style={{
            backgroundColor: 'var(--background)',
            borderColor: 'var(--border)',
            color: 'var(--foreground)',
            fontSize: 'var(--text-sm)',
            outline: 'none',
          }}
        />
      </div>

      {/* Row 2 on mobile / middle on desktop: intent filter pills.
          Horizontal scroll on narrow widths so all pills stay reachable
          even when viewport can't fit them — same pattern as the
          UnderlineTabs primitive uses. */}
      <div
        className="flex items-center gap-2 overflow-x-auto"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        <style>{`
          .prompt-filter-scroll::-webkit-scrollbar { display: none; }
        `}</style>
        <div className="prompt-filter-scroll flex items-center gap-2">
          <FilterButton
            label="All Intents"
            isActive={selectedIntent === 'all'}
            onClick={() => onIntentChange('all')}
          />
          {INTENT_CONFIG.map((intent) => (
            <FilterButton
              key={intent.key}
              label={intent.shortLabel}
              isActive={selectedIntent === intent.key}
              onClick={() => onIntentChange(intent.key)}
            />
          ))}
        </div>
      </div>

      {/* Row 3 on mobile / right on desktop: Add Prompt CTA */}
      <Button
        className="inline-flex items-center justify-center gap-2 w-full md:w-auto flex-shrink-0"
        onClick={onAddPrompt}
        style={{
          backgroundColor: 'var(--signal-blue)',
          color: 'white',
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-weight-medium)',
          border: 'none',
        }}
      >
        <Plus className="w-4 h-4" />
        Add Prompt
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter pill button — whitespace-nowrap so pills don't break mid-word when
// the parent scrolls horizontally
// ---------------------------------------------------------------------------

interface FilterButtonProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

function FilterButton({ label, isActive, onClick }: FilterButtonProps) {
  return (
    <button
      onClick={onClick}
      className="px-3 py-2 rounded-lg transition-all whitespace-nowrap flex-shrink-0"
      style={{
        backgroundColor: isActive ? 'var(--muted)' : 'transparent',
        border: isActive ? '1px solid var(--border)' : '1px solid transparent',
        fontSize: 'var(--text-sm)',
        fontWeight: isActive
          ? 'var(--font-weight-semibold)'
          : 'var(--font-weight-normal)',
        color: isActive ? 'var(--foreground)' : 'var(--muted-foreground)',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}